/**
 * Labour cost and metrics calculation utilities.
 * Used only in Admin contexts — never exposed to Production users.
 *
 * Single source of truth: calcTaskMetrics() produces all derived fields
 * for a task record. Call it at Admin edit time and in the cost-calculation hook.
 */

export function calcDurationMinutes(startTime, endTime) {
  if (!startTime || !endTime) return null;
  const start = startTime instanceof Date ? startTime : startTime.toDate?.() ?? new Date(startTime);
  const end = endTime instanceof Date ? endTime : endTime.toDate?.() ?? new Date(endTime);
  const diff = (end.getTime() - start.getTime()) / 1000 / 60;
  // Ceil so any real elapsed time counts as at least 1 minute (avoids misleading 0m
  // when datetime-local or sub-minute work would round down).
  return diff > 0 ? Math.ceil(diff) : null;
}

export function calcLabourMinutes(durationMinutes, teamSize) {
  if (!durationMinutes || !teamSize) return null;
  return durationMinutes * teamSize;
}

/**
 * Calculate labour cost from rates.
 *
 * @param {number} durationMinutes
 * @param {string[]} employeeIds - assigned employee UIDs (individual mode)
 * @param {number} teamSize
 * @param {Record<string,number>} userRates - map of uid → hourly rate
 * @param {number|null} defaultTeamRate - from adminSettings
 * @returns {{ cost: number|null, source: 'individualRates'|'defaultTeamRate'|'unavailable', appliedRate: number|null }}
 *   appliedRate is the combined hourly rate ($/hr for the whole crew) actually
 *   used. It is stamped onto the task so a locked historical cost stays
 *   auditable and provably reflects the wage in effect when the work was costed.
 */
export function calcLabourCost(durationMinutes, employeeIds, teamSize, userRates, defaultTeamRate) {
  if (!durationMinutes) return { cost: null, source: 'unavailable', appliedRate: null };

  const hours = durationMinutes / 60;

  if (employeeIds?.length > 0 && userRates) {
    // A rate of 0 is a valid (free/volunteer) rate, so test for presence with
    // != null rather than truthiness.
    const ratedCount = employeeIds.filter((uid) => userRates[uid] != null).length;

    // Individual mode with SOME but not all workers rated would silently
    // undercount the unrated workers' labour. Refuse to produce a misleading
    // cost: leave it null and flag 'partialRates' so the task stays in the
    // recalc queue (labourCost == null) and is surfaced as "needs rate" until
    // every assigned worker has a rate.
    if (ratedCount > 0 && ratedCount < employeeIds.length) {
      return { cost: null, source: 'partialRates', appliedRate: null };
    }

    if (ratedCount === employeeIds.length) {
      const totalRate = employeeIds.reduce((sum, uid) => sum + userRates[uid], 0);
      if (totalRate > 0) {
        return {
          cost: parseFloat((hours * totalRate).toFixed(2)),
          source: 'individualRates',
          appliedRate: parseFloat(totalRate.toFixed(2)),
        };
      }
    }
    // ratedCount === 0 → no individual rates available; fall back to team rate.
  }

  if (teamSize && defaultTeamRate) {
    return {
      cost: parseFloat((hours * teamSize * defaultTeamRate).toFixed(2)),
      source: 'defaultTeamRate',
      appliedRate: parseFloat((teamSize * defaultTeamRate).toFixed(2)),
    };
  }

  return { cost: null, source: 'unavailable', appliedRate: null };
}

export function calcUnitsPerHour(quantity, durationMinutes) {
  if (!quantity || !durationMinutes) return null;
  return parseFloat((quantity / (durationMinutes / 60)).toFixed(2));
}

export function calcUnitsPerLabourHour(quantity, labourMinutes) {
  if (!quantity || !labourMinutes) return null;
  return parseFloat((quantity / (labourMinutes / 60)).toFixed(2));
}

export function calcCostPerUnit(labourCost, quantity) {
  if (!labourCost || !quantity) return null;
  return parseFloat((labourCost / quantity).toFixed(4));
}

/**
 * Compute all derived metrics for a task in one call.
 * Used by the Admin edit flow and the auto-calculation hook.
 *
 * @param {object} task - Firestore task document data
 * @param {Record<string,number>} userRates - uid → hourlyRate (from userPrivateRates)
 * @param {number|null} defaultTeamRate - from adminSettings
 * @param {string} currency - ISO currency code
 * @param {Date} [now] - timestamp for labourCostCalculatedAt (defaults to new Date())
 * @returns {object} partial task fields to merge/update
 */
export function calcTaskMetrics(task, userRates, defaultTeamRate, currency, now = new Date()) {
  const start = task.startTime?.toDate?.() ?? (task.startTime ? new Date(task.startTime) : null);
  const end = task.endTime?.toDate?.() ?? (task.endTime ? new Date(task.endTime) : null);

  const durationMinutes = calcDurationMinutes(start, end);

  // Net working time excludes recorded downtime (breaks / stoppages from the
  // pause feature). Labour cost and labour-hours are based on net time so the
  // costed amount reflects actual work, not paid-out break time. durationMinutes
  // stays wall-clock for display and clock-throughput.
  const downtime = task.downtimeMinutes > 0 ? task.downtimeMinutes : 0;
  const netDurationMinutes = durationMinutes != null ? Math.max(0, durationMinutes - downtime) : null;

  const labourMinutes = calcLabourMinutes(netDurationMinutes, task.teamSize);
  const labourHours = labourMinutes != null ? parseFloat((labourMinutes / 60).toFixed(3)) : null;

  const qty = task.quantityCompleted ?? null;
  const unitsPerHour = calcUnitsPerHour(qty, durationMinutes);
  const unitsPerLabourHour = calcUnitsPerLabourHour(qty, labourMinutes);

  const { cost: labourCost, source: labourRateSource, appliedRate: labourRateApplied } = calcLabourCost(
    netDurationMinutes,
    task.employeeIds,
    task.teamSize,
    userRates,
    defaultTeamRate
  );

  const costPerUnit = calcCostPerUnit(labourCost, qty);

  return {
    durationMinutes,
    netDurationMinutes,
    labourMinutes,
    labourHours,
    unitsPerHour,
    unitsPerLabourHour,
    labourCost,
    labourCostCurrency: labourCost != null ? currency : null,
    labourRateSource,
    // Hourly rate ($/hr for the whole crew) used to produce labourCost. Stored
    // so the locked historical cost remains auditable and tied to the wage that
    // was in effect when the work was costed — not silently re-derived later.
    labourRateApplied,
    // Always stamp once processed — even when no rate is available. This stops
    // uncostable tasks being re-queried on every dashboard load. Tasks that still
    // need a rate are identified by labourCost == null (see useLabourCostCalculation
    // force mode), not by a null calculatedAt.
    labourCostCalculatedAt: now,
    costPerUnit,
  };
}

/**
 * Strip all admin-only financial fields from a task record before it enters
 * production-user React state. Firestore has no field-level security, so raw
 * task documents returned to production users may contain cost values written
 * by the admin labour-cost hook. This function ensures those values are never
 * present in any production-facing component tree, even if the Firestore
 * document contains them.
 *
 * This is a defence-in-depth measure. The underlying architectural fix is to
 * move cost data to a separate admin-only collection (taskCostReports).
 */
export function sanitizeProductionTask(task) {
  // eslint-disable-next-line no-unused-vars
  const {
    labourCost,
    labourCostCalculatedAt,
    labourRateSource,
    labourRateApplied,
    labourCostCurrency,
    costPerUnit,
    labourMinutes,
    labourHours,
    netDurationMinutes,
    ...safe
  } = task;
  return safe;
}

export function formatDuration(minutes) {
  if (minutes == null) return '—';
  if (minutes === 0) return '<1m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

/** True for completed tasks that look like accidental starts (legacy 0m records). */
export function isVeryShortCompletedTask(task) {
  return task?.status === 'completed' && (task.durationMinutes == null || task.durationMinutes < 1);
}

export function formatCurrency(amount, currency = 'CAD') {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}
